import React, { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';

import { DragDropContext, Droppable } from '@hello-pangea/dnd';
import { gsap } from 'gsap';
import update from 'immutability-helper';
import { Transition, TransitionGroup } from 'react-transition-group';

import { useAppDispatch, useAppSelector } from '@pkg/hooks';

import { useWindowResize } from '@app/helpers';
import { removeEmoji, updateEmojis } from '@app/store/actions/picker';

import './SelectedEmojis.scss';
import SelectedEmojisItem from './SelectedEmojisItem';

const YatPickerSearchResult = () => {
    const dispatch = useAppDispatch();

    const { tiles, status } = useAppSelector(({ picker }) => picker);
    const tileSpacerElement = useRef() as React.MutableRefObject<HTMLDivElement>;
    const [containerWidth, setContainerWidth] = useState(0);
    const deferredWidth = useDeferredValue(containerWidth);

    const windowSize = useWindowResize();

    useEffect(() => {
        if (windowSize !== undefined) {
            setContainerWidth(tileSpacerElement.current.offsetWidth * tiles.length);
        }
    }, [windowSize, tiles, tileSpacerElement]);

    const handleDeleteEmoji = useCallback(
        (removalIndex: number) => {
            dispatch(removeEmoji(removalIndex));
        },
        [dispatch],
    );

    const onDragEnd = useCallback(
        (result) => {
            const { destination, source } = result;

            if (!destination) return;

            if (destination.droppableId === source.droppableId && destination.index === source.index) {
                dispatch(updateEmojis(tiles));
            } else {
                dispatch(
                    updateEmojis(
                        update(tiles, {
                            $splice: [
                                [source.index, 1],
                                [destination.index, 0, tiles[source.index]],
                            ],
                        }),
                    ),
                );
            }
        },
        [dispatch, tiles],
    );

    const tileInitialAnimState = (node) => {
        gsap.set(node, { opacity: 0, scale: 0.75 });
    };

    const tileEnteringAnim = (node) => {
        gsap.timeline()
            .addLabel('run', 0.2)
            .to(node, { opacity: 1, duration: 0.25 }, 'run')
            .to(node, { scale: 1, duration: 1, ease: 'elastic.out(1.25, 0.3)' }, 'run');
    };

    const classNames = useMemo(() => {
        let classNames = 'picker-form-selected-emojis';
        if (tiles.length > 0) classNames += ' picker-form-selected-emojis--has-tiles';
        if (tiles.length > 3) classNames += ' picker-form-selected-emojis--has-many-tiles';

        return classNames;
    }, [tiles]);

    const nodeRef = useRef(null);
    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="droppable" direction="horizontal">
                {(provided) => (
                    <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={classNames}
                        style={{ width: `${deferredWidth}px` }}
                    >
                        <TransitionGroup component={null} nodeRef={nodeRef}>
                            {tiles.map((emoji, i) => (
                                <Transition
                                    key={i}
                                    nodeRef={nodeRef}
                                    timeout={{ enter: 1200, exit: 0 }}
                                    onEnter={tileInitialAnimState}
                                    onEntering={tileEnteringAnim}
                                >
                                    <SelectedEmojisItem
                                        key={i + emoji[1]}
                                        emoji={emoji}
                                        onRemove={() => handleDeleteEmoji(i)}
                                        id={i + ''}
                                        index={i}
                                        glowEnabled={status === 'YAT_DEFINED'}
                                    />
                                </Transition>
                            ))}
                        </TransitionGroup>
                        <div
                            ref={tileSpacerElement}
                            className="picker-form-selected-emojis__tile-spacer"
                            aria-hidden="true"
                        />
                        {provided.placeholder}
                    </div>
                )}
            </Droppable>
        </DragDropContext>
    );
};

export default YatPickerSearchResult;
